import { ThreadActivityLog } from "@/app/Threads/GraphQL/threadActivityQuery";
import { ThreadMessageFragment } from "@/app/Threads/GraphQL/threadMessageQueries";

export type ThreadTimelineEntry =
  | {
      kind: "message";
      id: string;
      created_at: string;
      message: ThreadMessageFragment;
    }
  | {
      kind: "activity";
      id: string;
      created_at: string;
      log: ThreadActivityLog;
    };

/**
 * Messages are paginated and the log is not, so activity is clamped to the
 * stretch of conversation actually loaded: an entry older than the oldest
 * loaded message would sit above the button that loads what comes before it.
 * Once every message is loaded there is nothing left to clamp against.
 */
export function interleaveThreadActivity(
  messages: ThreadMessageFragment[],
  logs: ThreadActivityLog[],
  hasOlderMessages: boolean,
): ThreadTimelineEntry[] {
  const oldestMessageAt = messages[0]?.created_at;

  const visibleLogs =
    hasOlderMessages && oldestMessageAt !== undefined
      ? logs.filter((log) => log.created_at >= oldestMessageAt)
      : logs;

  const entries: ThreadTimelineEntry[] = [
    ...messages.map(
      (message): ThreadTimelineEntry => ({
        kind: "message",
        id: `message-${message.id}`,
        created_at: message.created_at,
        message,
      }),
    ),
    ...visibleLogs.map(
      (log): ThreadTimelineEntry => ({
        kind: "activity",
        id: `activity-${log.id}`,
        created_at: log.created_at,
        log,
      }),
    ),
  ];

  // An event is what prompts the reply to it, so it sits above a message
  // sharing its timestamp.
  return entries.sort((a, b) => {
    if (a.created_at !== b.created_at) {
      return a.created_at < b.created_at ? -1 : 1;
    }

    if (a.kind !== b.kind) {
      return a.kind === "activity" ? -1 : 1;
    }

    return Number(a.id.split("-")[1]) - Number(b.id.split("-")[1]);
  });
}
